import { IElementStat } from "core-integration/src/store/element-stats/types";
import { IEvent } from "core-integration/src/store/events/types";
import { getStatAbbr } from "plos/src/utils/elementStats";
import { useTranslation } from "react-i18next";
import { SelectedOverlay } from "../components/PitchControls/types";
import { priceChangePredictionEnabled } from "../components/PriceChanges/utils";
import { IStatDetails, statDetails } from "../utils/stats";

/** Number of upcoming gameweeks rendered as columns for the FDR overlay. */
export const fdrEventCount = 5;

export type OverlayColumnVariant = "small" | "medium" | "wide";

export interface IOverlayColumn {
  key: string;
  label?: string;
  shortName: string;
  variant: OverlayColumnVariant;
}

interface GetOverlayColumnsOptions {
  stats: ReadonlyArray<IElementStat>;
  statsByName: Record<string, IElementStat>;
  fdrEvents: ReadonlyArray<IEvent>;
}

const getStatColumnDetails = (
  stat: IStatDetails,
  variant: OverlayColumnVariant = "small",
): IOverlayColumn => ({
  key: stat.key,
  label: stat.label,
  shortName: stat.shortName,
  variant,
});

/**
 * Returns the list-view columns to render for the active overlay.
 */
export const useOverlayColumns = (
  overlay: SelectedOverlay,
  { stats, statsByName, fdrEvents }: GetOverlayColumnsOptions,
): IOverlayColumn[] => {
  const { t } = useTranslation("common");
  const details = statDetails(t);

  switch (overlay) {
    case "opponent":
    case "form":
      return [
        getStatColumnDetails(details.form),
        getStatColumnDetails(details.event_points),
        getStatColumnDetails(details.total_points),
        {
          key: "fixture",
          label: t(
            "listView.fixtureNextGameweek.label",
            "Fixture next Gameweek",
          ),
          shortName: t("listView.fixtureNextGameweek.shortName", "Fix"),
          variant: "medium",
        },
      ];
    case "currentPrice":
    case "sellingPrice":
      return [
        getStatColumnDetails({
          ...details.now_cost,
          label: t("listView.currentPrice.label", "Current Price"),
          shortName: t("listView.currentPrice.shortName", "CP"),
        }),
        {
          key: "purchase_price",
          label: t("listView.purchasePrice.label", "Purchase Price"),
          shortName: t("listView.purchasePrice.shortName", "PP"),
          variant: "small",
        },
        {
          key: "selling_price",
          label: t("listView.sellingPrice.label", "Selling Price"),
          shortName: t("listView.sellingPrice.shortName", "SP"),
          variant: "small",
        },
        getStatColumnDetails(details.form),
        getStatColumnDetails(details.total_points),
        {
          key: "fixture",
          label: t(
            "listView.fixtureNextGameweek.label",
            "Fixture next Gameweek",
          ),
          shortName: t("listView.fixtureNextGameweek.shortName", "Fix"),
          variant: "medium",
        },
      ];
    case "fdr":
      return fdrEvents.map((event) => ({
        key: `fdr-${event.id}`,
        label: t("listView.fdrGameweek.label", "Gameweek {{gw}}", {
          gw: event.id,
        }),
        shortName: t("listView.fdrGameweek.shortName", "GW{{gw}}", {
          gw: event.id,
        }),
        variant: "medium",
      }));
    case "ownership":
      return [getStatColumnDetails(details.selected_by_percent)];
    case "points":
      return [
        {
          key: "points",
          label: t("listView.points.label", "Points"),
          shortName: t("listView.points.shortName", "Pts"),
          variant: "small",
        },
        ...stats.map((stat): IOverlayColumn => ({
          key: stat.name,
          label: stat.label,
          shortName: getStatAbbr(stat.name, statsByName),
          variant: "small",
        })),
      ];
    case "price_change":
      return [
        {
          key: "status",
          shortName: t("listView.status.shortName", "Status"),
          variant: "wide",
        },
        {
          key: "progress",
          shortName: t("listView.progress.shortName", "Progress"),
          variant: "medium",
        },
        ...(priceChangePredictionEnabled
          ? ([
              {
                key: "predicted_progress",
                shortName: t(
                  "listView.predictedProgress.shortName",
                  "Predicted Progress",
                ),
                variant: "wide",
              },
            ] as IOverlayColumn[])
          : []),
        {
          key: "ownership_trend",
          shortName: t("listView.ownershipTrend.shortName", "Ownership Trend"),
          variant: "wide",
        },
        getStatColumnDetails({
          ...details.now_cost,
          label: t("listView.currentPrice.label", "Current Price"),
          shortName: t("listView.currentPrice.shortName", "CP"),
        }),
        {
          key: "purchase_price",
          label: t("listView.purchasePrice.label", "Purchase Price"),
          shortName: t("listView.purchasePrice.shortName", "PP"),
          variant: "medium",
        },
        {
          key: "selling_price",
          label: t("listView.sellingPrice.label", "Selling Price"),
          shortName: t("listView.sellingPrice.shortName", "SP"),
          variant: "medium",
        },
      ];
    default:
      return [];
  }
};
